import apiEndPoints from "../../routeConfig";
import { ApiService } from "../../../service/api.service";
import { attempt } from "../../../service/apiResult";

const apiService = new ApiService();

export const ecosytemIntegatorCreateOrUpdate = async (data, editMode, id) => {
  try {
    const url = editMode
      ? `${apiEndPoints.ecosytemIntegatorCreateOrUpdate}/${id}`
      : apiEndPoints.ecosytemIntegatorCreateOrUpdate;

    const response = editMode
      ? await apiService.update(url, data)
      : await apiService.post(url, data);

    return {
      statusCode: editMode ? 200 : 201,
      message: editMode ? 'Ecosystem Integrator updated successfully' : 'Ecosystem Integrator created successfully',
      data: response
    };
  } catch (error) {
    return error;
  }
}

export const getAllEcosytemIntegator = async (page = 0, size = 10) => {
  try {
    const response = await apiService.get(`${apiEndPoints.getAllEcosytemIntegator}?page=${page}&size=${size}`,);
    return response;
  } catch (error) {
    return error;
  }
};

export const ecosytemIntegatorGetById = async (id) => {
  let result;
  await apiService.get(`${apiEndPoints.ecosytemIntegatorGetById}/${id}`)
    .then((response) => {
      result = response.data;
    })
    .catch(error => {
      result = error;
    });
  return result;
};

export const deleteEcosytemIntegator = async (id) =>
  attempt(() => apiService.patch(`${apiEndPoints.deleteEcosytemIntegator}/${id}/status/DELETED`, ''));

/**
 * The integrator types with their real database ids.
 *
 * `/metadata` returns the type CODES only — an array of strings, no ids — which is why
 * this screen used to map a code to an id from a hardcoded table copied out of the
 * baseline seed. An id guessed that way is right only while every environment happens to
 * have been seeded in the same order, and a wrong one is invisible: the update is accepted
 * and the type simply does not change.
 *
 * Each row is `{ id, name, category, appUrl }`, where `category` is the SNAKE_CASE code.
 * Settings → Integrator Redirects reads the same endpoint.
 */
export const ecosytemIntegatorTypesWithIds = async () => {
  try {
    const response = await apiService.get(apiEndPoints.ecosytemIntegatorTypes);
    return Array.isArray(response?.data) ? response.data : [];
  } catch (error) {
    return [];
  }
};

export const ecosytemIntegatorMetaData = async () => {
  try {
    const response = await apiService.get(`${apiEndPoints.ecosytemIntegatorMetaData}`);
    const integratorTypeList = {};
    if (response.data?.ecosystemTypes && Array.isArray(response.data.ecosystemTypes)) {
      response.data.ecosystemTypes.forEach(type => {
        // Convert SNAKE_CASE to Sentence case
        const sentenceCase = type
          .toLowerCase()
          .replace(/_/g, ' ')
          .replace(/\b\w/g, char => char.toUpperCase());
        integratorTypeList[type] = sentenceCase;
      });
    }
    return {
      statusCode: 200,
      data: {
        integratorTypeList: integratorTypeList,
        ecosystemTypes: response.data?.ecosystemTypes || []
      }
    };
  } catch (error) {
    return error;
  }
};


/**
 * Initiatives offered in a country, `[{ id, name, initiativeType }]`: the picker for a new
 * Initiative Based integrator, which is tied to the one chosen when it is created.
 */
export const getInitiativeOptions = async (countryId) => {
  try {
    const response = await apiService.get(`${apiEndPoints.ecosytemIntegatorGetById}/initiative-options?countryId=${countryId}`);
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  } catch (error) {
    return [];
  }
};

/** The Initiatives the integrator takes part in, `[{ id, name, initiativeType }]`. */
export const getIntegratorInitiatives = async (integratorId) => {
  try {
    const response = await apiService.get(`${apiEndPoints.ecosytemIntegatorGetById}/${integratorId}/initiatives`);
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  } catch (error) {
    return [];
  }
};

export const getIntegratorSubUsers = async (integratorId) => {
  try {
    const response = await apiService.get(`${apiEndPoints.ecosytemIntegatorGetById}/${integratorId}/sub-users`);
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  } catch (error) {
    return [];
  }
};

export const addIntegratorSubUser = async (integratorId, payload) => {
  try {
    const response = await apiService.post(
      `${apiEndPoints.ecosytemIntegatorCreateOrUpdate}/${integratorId}/sub-users`,
      payload
    );
    return { success: true, data: response };
  } catch (error) {
    return { success: false, message: error?.response?.data?.message || error.message || 'Failed to add sub-user' };
  }
};

export const removeIntegratorSubUser = async (integratorId, subUserId) => {
  try {
    await apiService.delete(`${apiEndPoints.ecosytemIntegatorCreateOrUpdate}/${integratorId}/sub-users/${subUserId}`);
    return { success: true };
  } catch (error) {
    return { success: false, message: error?.response?.data?.message || error.message || 'Failed to remove sub-user' };
  }
};


// Initiative approval chain (P2-3): one partner's separate integrator accounts (area-based and
// Initiative Based) linked as one organisation. Display only.
const linkedAccountsUrl = (integratorId) =>
  `${apiEndPoints.ecosytemIntegatorGetById}/${integratorId}/linked-accounts`;

export const getLinkedIntegrators = async (integratorId) =>
  attempt(() => apiService.get(linkedAccountsUrl(integratorId)).then((response) => response.data));

export const getLinkableIntegrators = async (integratorId) =>
  attempt(() => apiService.get(`${linkedAccountsUrl(integratorId)}/candidates`).then((response) => response.data));

export const linkIntegrator = async (integratorId, otherIntegratorId) =>
  attempt(() => apiService.post(linkedAccountsUrl(integratorId), { integratorId: otherIntegratorId }));

export const unlinkIntegrator = async (integratorId, otherIntegratorId) =>
  attempt(() => apiService.delete(`${linkedAccountsUrl(integratorId)}/${otherIntegratorId}`));

export const countryMetaData = async () => {
  let result;
  await apiService.get(`${apiEndPoints.countryMetaData}`)
    .then((response) => {
      result = response.data;
    })
    .catch(error => {
      result = error;
    });
  return result;
};
// eK Score commercial terms of the integrator's ecosystem (subscription tiers + farmer share).
const ekScoreTermsUrl = (ecosystemId) => `${apiEndPoints.ekScoreCommercialTerms}/${ecosystemId}/ekscore-commercial-terms`;

export const getEkScoreCommercialTerms = async (ecosystemId) =>
  attempt(() => apiService.get(ekScoreTermsUrl(ecosystemId)).then((response) => response.data));

export const saveEkScoreCommercialTerms = async (ecosystemId, payload) =>
  attempt(() => apiService.update(ekScoreTermsUrl(ecosystemId), payload));
